import { useState, useEffect, useCallback, useMemo } from 'react';

/**
 * Hook para manejar la instalación de PWA
 * Retorna el estado de instalación y funciones para instalar
 */
const usePWAInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isInstallable, setIsInstallable] = useState(false);

  // Detectar si es iOS
  const isIOS = useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  }, []);

  // Detectar si ya está instalada (modo standalone)
  const isStandalone = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(display-mode: standalone)').matches || 
           window.navigator.standalone === true;
  }, []);

  useEffect(() => {
    // Si ya está en modo standalone, está instalada
    if (isStandalone) {
      setIsInstalled(true);
      return;
    }

    // Evento beforeinstallprompt (Android/Chrome/Edge)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    // Evento cuando se instala la app
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // En iOS siempre es "instalable" (manualmente)
    if (isIOS && !isStandalone) {
      setIsInstallable(true);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [isIOS, isStandalone]);

  // Función para instalar (Android/Chrome)
  const installApp = useCallback(async () => {
    if (!deferredPrompt) {
      return { success: false, reason: 'no-prompt' };
    }

    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        setIsInstalled(true);
        setIsInstallable(false);
        setDeferredPrompt(null);
        return { success: true, outcome };
      }
      
      return { success: false, outcome };
    } catch (error) {
      console.error('[PWA] Error al instalar:', error);
      return { success: false, reason: 'error', error };
    }
  }, [deferredPrompt]);

  return {
    isInstallable,
    isInstalled,
    isIOS,
    isStandalone,
    canPrompt: !!deferredPrompt,
    installApp
  };
};

export default usePWAInstall;
